// src/utils/spendingLimit.js
// Purchase-limit warnings for fans (STEP3_5A_TIERS_DESIGN.md 3.5.9, 3.7 and
// 5.14; release R3, dark in 'cap' mode).
//
// Display only. Every number comes from the server: the `limit` object a
// setup sends in 'limits' mode (getFloodPaymentSetup, getSuperCritSetup,
// getTeamSuperCritSetup), the `details.limit` of a tips-limit refusal, the
// card-limit / card-blocked refusal tokens, and the getTipPurchaseOutcome
// reason after a held Checkout payment. In 'cap' mode the server sends none
// of these, so nothing here renders and every surface behaves as before.
//
// Copy rules (5.14): no string names, counts or describes another account; a
// card line never shows an amount or a time; "no daily limit", never "no
// limit"; the verify call to action follows the account's `verify` state
// (L11 start, L11s support, L11r in_review; a missing value reads as start).
//
// The membership strings (L1m-L4m, L2s, L10a-L10d, F4c, L14) ship with R5;
// they live here so the web harness pins every 5.14 string in one place.
//
// Zero imports: unit-tested in plain node
// (web-landing/scripts/test-spending-limit.mjs).

export const LINE_CENTS = 50000;               // the unverified line (TIP_DAILY_CAP_CENTS)
export const VERIFY_ROUTE = 'IdVerification';
export const SUPPORT_EMAIL = 'support@critcash.com';
export const SUPPORT_HREF = `mailto:${SUPPORT_EMAIL}`;

// Buttons (5.14 L5, L11, L11s).
export const USE_ANOTHER_CARD_LABEL = 'Use another card';
export const VERIFY_BUTTON_LABEL = 'Verify your ID and age';
export const VERIFY_BUTTON_A11Y = 'Verify your identity and age to remove the 24-hour limit';
export const CONTACT_SUPPORT_LABEL = 'Contact support';

// L11s / L11r follow a line whose verify clause was dropped.
export const SUPPORT_SENTENCE = `Contact ${SUPPORT_EMAIL} to finish your age check and remove the limit.`;
export const IN_REVIEW_SENTENCE = "Your verification is being reviewed. The limit comes off once it's approved.";

// L9: while getTipPurchaseOutcome is polled, then still pending at 20 s.
export const CONFIRMING_TEXT = 'Confirming your payment...';
export const STILL_CONFIRMING_TEXT = "Your payment is still being confirmed. It shows here once it goes through; if it doesn't go through, you won't be charged.";

// L12-L14: the server sentences (old bundles show them as they are).
export const SERVER_SENTENCES = {
  tipsLimit: "tips-limit: That's over the 24-hour limit for an unverified account. Verify your account to remove the limit.",
  cardLimit: "card-limit: This card can't be used for that purchase on an unverified account right now. Try another card, or verify your account to remove the limit.",
  cardBlocked: `card-blocked: This card can't be used for Super Crits, Rumbles or new memberships on CritCast. Please use a different card. If you think this is a mistake, contact ${SUPPORT_EMAIL}.`,
  membershipLimit: "membership-limit: That's over the 24-hour limit for an unverified account. Verify your account to remove the limit.",
};

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

// The house wallet formatter: "$120.00", "$1,234.50".
export function formatCurrency(cents) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((Number(cents) || 0) / 100);
}

// 3.5.9 display: minutes rounded up, "5h 20m", "20m" under an hour, never
// below "1m" (also for a time already reached: the client re-fetches then).
export function formatCooldown(ms) {
  const n = Number(ms);
  let mins = Number.isFinite(n) ? Math.ceil(n / 60000) : 1;
  if (mins < 1) mins = 1;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ---------------------------------------------------------------------------
// Verify call to action (L11, L11s, L11r)
// ---------------------------------------------------------------------------

const VERIFY_STATES = new Set(['start', 'support', 'in_review']);

export function verifyStateOf(verify) {
  return VERIFY_STATES.has(verify) ? verify : 'start';
}

// { kind, sentence (null for start: the line keeps its own verify clause),
//   button (null for in_review) }.
export function verifyLine(verify) {
  const kind = verifyStateOf(verify);
  if (kind === 'support') {
    return { kind, sentence: SUPPORT_SENTENCE, button: { kind: 'support', label: CONTACT_SUPPORT_LABEL, href: SUPPORT_HREF } };
  }
  if (kind === 'in_review') return { kind, sentence: IN_REVIEW_SENTENCE, button: null };
  return {
    kind,
    sentence: null,
    button: { kind: 'verify', label: VERIFY_BUTTON_LABEL, accessibilityLabel: VERIFY_BUTTON_A11Y, route: VERIFY_ROUTE },
  };
}

// The verify clause a line ends with: ", or verify (your account) to remove
// the limit." becomes "."; " Verify your account to remove the limit." goes.
const CLAUSE_OR = /, or verify(?: your account)? to remove the limit\.$/;
const CLAUSE_SENTENCE = /\s?Verify your account to remove the limit\.$/;

export function hasVerifyClause(text) {
  return CLAUSE_OR.test(text) || CLAUSE_SENTENCE.test(text);
}

// A line with a verify clause, adjusted for the account's verify state.
// Returns { text, verify, button }.
export function withVerify(text, verify) {
  const v = verifyLine(verify);
  if (!hasVerifyClause(text)) return { text, verify: v.kind, button: null };
  if (v.kind === 'start') return { text, verify: v.kind, button: v.button };
  const base = CLAUSE_OR.test(text) ? text.replace(CLAUSE_OR, '.') : text.replace(CLAUSE_SENTENCE, '');
  return { text: `${base} ${v.sentence}`, verify: v.kind, button: v.button };
}

// ---------------------------------------------------------------------------
// The 5.14 strings, raw (verify clause included; withVerify adjusts them)
// ---------------------------------------------------------------------------

export const LINES = {
  L1: (remaining) => `You have ${remaining} left right now in your 24-hour limit. Verify your account to remove the limit.`,
  L2: (remaining) => `That's more than the ${remaining} you have left right now in your 24-hour limit. Pick a smaller amount, or verify your account to remove the limit.`,
  L3: (cooldown) => `You've reached $500 in 24 hours on an unverified account. You can send more in ${cooldown}, or verify to remove the limit.`,
  L3b: (remaining, cooldown) => `You have ${remaining} left in your 24-hour limit, less than the smallest amount here. You can send more in ${cooldown}, or verify to remove the limit.`,
  L3p: (cooldown) => `A payment you started recently is still counted toward your 24-hour limit. You can send more in ${cooldown} or sooner, or verify to remove the limit.`,
  L4: () => "You've reached $500 in 24 hours on an unverified account. Verify your account to remove the limit.",
  L1m: (remaining) => LINES.L1(remaining),
  L2m: (remaining) => `More than the ${remaining} you have left right now in your 24-hour limit.`,
  L3m: (cooldown) => `You've reached $500 in 24 hours on an unverified account. You can join in ${cooldown}, or verify to remove the limit.`,
  L3bm: (remaining, cooldown) => `You have ${remaining} left in your 24-hour limit, less than this membership's first payment. You can join in ${cooldown}, or verify to remove the limit.`,
  L3pm: (cooldown) => `A payment you started recently is still counted toward your 24-hour limit. You can join in ${cooldown} or sooner, or verify to remove the limit.`,
  L4m: () => LINES.L4(),
  L2s: (to, diff, remaining) => `Switching to ${to} counts ${diff} toward your 24-hour limit, more than the ${remaining} you have left right now. Pick a lower tier, or verify your account to remove the limit.`,
  L5: () => "This card can't be used for that purchase on an unverified account right now. Try another card, or verify your account to remove the limit.",
  L5s: () => "Your saved card can't be used for purchases here on an unverified account right now. Try another card, or verify your account to remove the limit.",
  L6: () => `This card can't be used for Super Crits, Rumbles or new memberships on CritCast. Please use a different card. If you think this is a mistake, contact ${SUPPORT_EMAIL}.`,
  L7: () => "Your payment didn't go through and you weren't charged. This card can't be used for that purchase on an unverified account right now. Your bank may show a pending authorization for a few days; it goes away on its own. Try another card, or verify your account to remove the limit.",
  L8: () => `Your payment didn't go through and you weren't charged. This card can't be used for Super Crits, Rumbles or new memberships on CritCast. Your bank may show a pending authorization for a few days; it goes away on its own. If you think this is a mistake, contact ${SUPPORT_EMAIL}.`,
  L8b: () => "Your payment didn't go through and you weren't charged: it was over the 24-hour limit for an unverified account. Your bank may show a pending authorization for a few days; it goes away on its own. Verify your account to remove the limit.",
  L10a: (creatorName, cardPhrase, price, creditClause) => `Your membership to ${creatorName}'s channel didn't start because ${cardPhrase} can't be used for that membership on an unverified account right now. We refunded your payment of ${price} in full${creditClause}; the refund usually shows within 5 to 10 business days, or the charge drops off your statement. Verify your account to remove the limit.`,
  L10b: (creatorName, cardPhrase, price, creditClause) => `Your membership to ${creatorName}'s channel didn't start because ${cardPhrase} can't be used for new memberships on CritCast. We refunded your payment of ${price} in full${creditClause}; the refund usually shows within 5 to 10 business days, or the charge drops off your statement. If you think this is a mistake, contact ${SUPPORT_EMAIL}.`,
  L10c: () => "Your last payment was refunded: that card can't be used for that membership on an unverified account right now. Use a different card, or verify your account to remove the limit.",
  L10d: () => "Your last payment was refunded: that card can't be used for new memberships on CritCast. Please use a different card.",
  F4c: (diff) => `On an unverified account, this switch counts ${diff} toward your 24-hour limit now. Verify your account to remove the limit.`,
};

// ---------------------------------------------------------------------------
// Account limit lines (L1-L4; L1m-L4m on the join panel)
// ---------------------------------------------------------------------------

// limit: a setup's `limit` ({limited, remainingCents, nextFreeAtMs, needCents,
// settledOnly, verify, card}) or a refusal's details.limit ({remainingCents,
// nextFreeAtMs, needCents, settledOnly, verify}; a refusal is always limited).
// A missing settledOnly reads as false (L3p, the line that is true either way).
// opts: { surface: 'tip' | 'join', amountCents (the picked amount, for L2),
//         nowMs }.
// Returns null (nothing to show: no limit object, a verified or admin payer,
// or nothing used yet) or { id, text, verify, button, atLimit, nextFreeAtMs }.
// atLimit: the room is below the smallest amount on this surface, so the
// surface sends nothing until nextFreeAtMs (or a re-fetch).
export function limitLines(limit, opts = {}) {
  if (!limit || typeof limit !== 'object' || limit.limited === false) return null;
  const remaining = limit.remainingCents;
  if (!isNum(remaining)) return null;
  const join = opts.surface === 'join';
  const need = isNum(limit.needCents) && limit.needCents > 0 ? limit.needCents : 100;
  const nowMs = isNum(opts.nowMs) ? opts.nowMs : Date.now();
  const room = Math.max(0, remaining);
  const at = isNum(limit.nextFreeAtMs) ? limit.nextFreeAtMs : null;
  const out = (id, raw, atLimit) => ({ id, ...withVerify(raw, limit.verify), atLimit, nextFreeAtMs: atLimit ? at : null });

  if (room < need) {
    if (at == null) return out(join ? 'L4m' : 'L4', LINES.L4(), true);
    const cooldown = formatCooldown(at - nowMs);
    if (limit.settledOnly !== true) return out(join ? 'L3pm' : 'L3p', (join ? LINES.L3pm : LINES.L3p)(cooldown), true);
    if (room <= 0) return out(join ? 'L3m' : 'L3', (join ? LINES.L3m : LINES.L3)(cooldown), true);
    return out(join ? 'L3bm' : 'L3b', (join ? LINES.L3bm : LINES.L3b)(formatCurrency(room), cooldown), true);
  }
  const amt = opts.amountCents;
  if (isNum(amt) && amt > room) {
    return join
      ? { id: 'L2m', text: LINES.L2m(formatCurrency(room)), verify: verifyStateOf(limit.verify), button: null, atLimit: false, nextFreeAtMs: null }
      : out('L2', LINES.L2(formatCurrency(room)), false);
  }
  if (room < LINE_CENTS) return out(join ? 'L1m' : 'L1', LINES.L1(formatCurrency(room)), false);
  return null;
}

// True when the amount fits the limit (or no limit applies). For the Super
// Crit tiers above the room (3.7: disabled).
export function fitsLimit(limit, amountCents) {
  if (!limit || typeof limit !== 'object' || limit.limited === false || !isNum(limit.remainingCents)) return true;
  return Number(amountCents) <= Math.max(0, limit.remainingCents);
}

// ---------------------------------------------------------------------------
// Card lines (L5, L5s, L6) and Checkout outcomes (L7, L8, L8b)
// ---------------------------------------------------------------------------

// status: 'card_limit' | 'card_blocked' (a refusal token, or a setup's
// limit.card.status). opts.saved: the line is about the saved card in a setup
// (L5s). Returns null for 'ok' / anything else, or { id, text, verify,
// button, retryLabel }. Never an amount or a time.
export function cardLine(status, opts = {}) {
  if (status === 'card_blocked') {
    return { id: 'L6', text: LINES.L6(), verify: verifyStateOf(opts.verify), button: null, retryLabel: USE_ANOTHER_CARD_LABEL };
  }
  if (status === 'card_limit') {
    const id = opts.saved ? 'L5s' : 'L5';
    return { id, ...withVerify(LINES[id](), opts.verify), retryLabel: USE_ANOTHER_CARD_LABEL };
  }
  return null;
}

// The saved card's status from a setup's limit object ('ok' when absent).
export function savedCardStatus(limit) {
  const s = limit && limit.card && limit.card.status;
  return s === 'card_limit' || s === 'card_blocked' ? s : 'ok';
}

// reason: getTipPurchaseOutcome's reason on a failed held payment. Returns
// null for any other reason (the surface shows its own failure text).
export function captureOutcomeLine(reason, opts = {}) {
  if (reason === 'card_limit') return { id: 'L7', ...withVerify(LINES.L7(), opts.verify) };
  if (reason === 'card_blocked') return { id: 'L8', text: LINES.L8(), verify: verifyStateOf(opts.verify), button: null };
  if (reason === 'account_limit') return { id: 'L8b', ...withVerify(LINES.L8b(), opts.verify) };
  return null;
}

// ---------------------------------------------------------------------------
// Refusals on the wire
// ---------------------------------------------------------------------------

// 'card_blocked' | 'card_limit' | null from a callable error's message token.
export function cardStatusFromError(err) {
  const raw = String((err && err.message) || '');
  if (raw.includes('card-blocked')) return 'card_blocked';
  if (raw.includes('card-limit')) return 'card_limit';
  return null;
}

// The details.limit a 'limits'-mode tips-limit refusal carries, else null
// ('cap' mode sends none: the surface keeps today's sentence).
export function limitFromError(err) {
  const raw = String((err && err.message) || '');
  if (!raw.includes('tips-limit')) return null;
  const d = err && err.details;
  const lim = d && typeof d === 'object' ? d.limit : null;
  return lim && typeof lim === 'object' && isNum(lim.remainingCents) ? lim : null;
}

// ---------------------------------------------------------------------------
// Membership lines (R5; unused in R3)
// ---------------------------------------------------------------------------

// L2s: a switch up whose counted difference is above the room.
export function switchLimitLine({ toName, diffCents, remainingCents, verify } = {}) {
  return { id: 'L2s', ...withVerify(LINES.L2s(toName, formatCurrency(diffCents), formatCurrency(Math.max(0, remainingCents || 0))), verify) };
}

// F4c: the switch-up count above the button (payer the mode limits).
export function switchCountLine({ diffCents, verify } = {}) {
  return { id: 'F4c', ...withVerify(LINES.F4c(formatCurrency(diffCents)), verify) };
}

// L10a / L10b: a refunded membership first payment. last4 null gives "the
// card you used"; creditCents > 0 adds the account-credit clause.
export function joinRefundLine({ reason, creatorName, last4, priceCents, creditCents, verify } = {}) {
  const cardPhrase = last4 ? `the card you used (ending ${last4})` : 'the card you used';
  const creditClause = Number(creditCents) > 0 ? `, and returned ${formatCurrency(creditCents)} to your account credit` : '';
  if (reason === 'card_blocked') {
    return { id: 'L10b', text: LINES.L10b(creatorName, cardPhrase, formatCurrency(priceCents), creditClause), verify: verifyStateOf(verify), button: null };
  }
  if (reason === 'card_limit') {
    return { id: 'L10a', ...withVerify(LINES.L10a(creatorName, cardPhrase, formatCurrency(priceCents), creditClause), verify) };
  }
  return null;
}

// L10c / L10d: the join panel while viewer.lastJoinRefused is set.
export function joinRefusedLine(reason, opts = {}) {
  if (reason === 'card_blocked') return { id: 'L10d', text: LINES.L10d(), verify: verifyStateOf(opts.verify), button: null };
  if (reason === 'card_limit') return { id: 'L10c', ...withVerify(LINES.L10c(), opts.verify) };
  return null;
}
